// «Креативы → Креативы сети»: готовые ролики из кабинета buyo по офферам каталога. Владелец скачивает их
// из кабинета и загружает сюда сам — пачкой (выбрал 3–5 файлов или перетащил на строку оффера).
// Сверху — офферы без креативов, новые офферы (7 дней) помечены: сразу видно, где пусто.
import { Button, Chip, EmptyState, Meter, Skeleton, toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clapperboard, Search, Upload, X } from 'lucide-react';
import { type DragEvent, useMemo, useRef, useState } from 'react';
import { api } from '../../api';
import { FilterBar, FilterSelect } from '../../filters';
import { PageHead } from '../common';
import { CreativeTabs } from './CreativeTabs';
import { mediaUrl } from './data';
import { uploadFile } from './Library';
import './creatives.css';

export interface NetworkOffer {
  extId: string;
  name: string;
  image: string | null;
  inSeason: boolean;
  approvePct: number | null;
  offerSlug: string | null;
  isNew: boolean;
  firstSeenAt: string;
  creatives: number;
  lastUpload: string | null;
  posters: string[];
}

interface UploadItem {
  id: string;
  name: string;
  progress: number;
  error?: string;
}

type Filter = 'empty' | 'new' | 'all';

export function useNetworkOffers() {
  return useQuery({
    queryKey: ['network-offers'],
    queryFn: () => api<NetworkOffer[]>('/api/library/network'),
  });
}

/** Сколько файлов грузить одновременно: пачка быстрее, но канал с телефона не забиваем. */
const PARALLEL = 2;

function OfferRow({ o }: { o: NetworkOffer }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [drag, setDrag] = useState(false);

  const upload = async (files: FileList | File[]) => {
    const list = [...files].filter((f) => f.type.startsWith('video/') || f.type.startsWith('image/'));
    if (!list.length) return toast('Нужны видео (или картинки) креативов');
    const items = list.map((f, i) => ({ f, id: `${Date.now()}-${i}-${f.name}` }));
    setUploads((u) => [...u, ...items.map(({ f, id }) => ({ id, name: f.name, progress: 0 }))]);
    let ok = 0;
    let next = 0;
    const worker = async () => {
      while (next < items.length) {
        const it = items[next++];
        if (!it) break;
        try {
          await uploadFile(
            it.f,
            (p) => setUploads((u) => u.map((x) => (x.id === it.id ? { ...x, progress: p } : x))),
            { network: o.extId, kind: 'footage' },
          );
          ok++;
          setUploads((u) => u.filter((x) => x.id !== it.id));
        } catch (e) {
          setUploads((u) => u.map((x) => (x.id === it.id ? { ...x, error: (e as Error).message } : x)));
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL, items.length) }, worker));
    if (ok) toast(`${o.name}: добавлено роликов — ${ok}. Описание будет через пару минут`);
    await qc.invalidateQueries({ queryKey: ['network-offers'] });
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer.files.length) void upload(e.dataTransfer.files);
  };

  return (
    <li
      className={`net-row${drag ? ' on' : ''}${o.creatives === 0 ? ' no-creo' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setDrag(false)}
      onDrop={onDrop}
    >
      <div className="net-img">
        {o.image ? <img src={o.image} alt="" loading="lazy" /> : <Clapperboard />}
      </div>
      <div className="net-main">
        <div className="net-title">
          <b>{o.name}</b>
          {o.isNew && (
            <Chip tone="info" dot>
              Новый
            </Chip>
          )}
          {o.inSeason && <Chip>В сезоне</Chip>}
          {o.offerSlug && <Chip>наш оффер: {o.offerSlug}</Chip>}
        </div>
        <p className="net-sub">
          {o.creatives > 0
            ? `Креативов сети: ${o.creatives} · последний ${when(o.lastUpload ?? o.firstSeenAt)}`
            : `Креативов сети нет · в каталоге с ${when(o.firstSeenAt)}`}
          {o.approvePct !== null ? ` · апрув сети ${o.approvePct.toFixed(1)}%` : ''}
        </p>
        {o.posters.length > 0 && (
          <div className="net-posters">
            {o.posters.map((p) => (
              <img key={p} src={mediaUrl(p) ?? ''} alt="" loading="lazy" />
            ))}
          </div>
        )}
        {uploads.length > 0 && (
          <ul className="uploads">
            {uploads.map((u) => (
              <li key={u.id}>
                <span>{u.name}</span>
                {u.error ? (
                  <>
                    <span className="neg">{u.error}</span>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label="Убрать"
                      onClick={() => setUploads((x) => x.filter((y) => y.id !== u.id))}
                    >
                      <X />
                    </button>
                  </>
                ) : (
                  <Meter value={u.progress} label={`Загрузка ${u.name}`} />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="net-act">
        <input
          ref={input}
          type="file"
          multiple
          accept="video/*,image/*"
          hidden
          onChange={(e) => {
            if (e.target.files) void upload(e.target.files);
            e.target.value = '';
          }}
        />
        <Button
          size="sm"
          variant={o.creatives === 0 ? 'primary' : 'default'}
          icon={<Upload />}
          onClick={() => input.current?.click()}
        >
          Добавить ролики
        </Button>
      </div>
    </li>
  );
}

export function NetworkPage() {
  const q = useNetworkOffers();
  const [filter, setFilter] = useState<Filter>('empty');
  const [text, setText] = useState('');
  const all = q.data ?? [];
  const counts = {
    empty: all.filter((o) => o.creatives === 0).length,
    new: all.filter((o) => o.isNew).length,
    all: all.length,
  };
  const shown = useMemo(() => {
    const t = text.trim().toLowerCase();
    return all
      .filter((o) => (filter === 'empty' ? o.creatives === 0 : filter === 'new' ? o.isNew : true))
      .filter((o) => !t || o.name.toLowerCase().includes(t) || (o.offerSlug ?? '').includes(t));
  }, [all, filter, text]);
  const newEmpty = all.filter((o) => o.isNew && o.creatives === 0).length;

  return (
    <>
      <PageHead
        title="Креативы"
        sub={`Креативы сети · ${counts.all - counts.empty} из ${counts.all} офферов с роликами из кабинета buyo`}
      />
      <CreativeTabs active="/creatives/network" />
      <div className="pc">
        {newEmpty > 0 && (
          <div className="notice info net-note">
            <Clapperboard aria-hidden />
            <p>
              Новых офферов без креативов: <b className="tnum">{newEmpty}</b>. Скачайте ролики в кабинете buyo
              и перетащите их на строку оффера — можно сразу несколько файлов.
            </p>
          </div>
        )}
        <FilterBar
          search={
            <label className="search-field filter-end">
              <Search aria-hidden />
              <input
                type="search"
                placeholder="Найти оффер"
                aria-label="Найти оффер сети"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </label>
          }
        >
          <FilterSelect<Filter>
            label="Показать"
            value={filter}
            onChange={setFilter}
            allValue="all"
            showValue
            options={[
              { value: 'empty', label: 'Без креативов', count: counts.empty },
              { value: 'new', label: 'Новые', count: counts.new },
              { value: 'all', label: 'Все', count: counts.all },
            ]}
          />
        </FilterBar>
        {q.isPending ? (
          <Skeleton h={320} />
        ) : q.isError ? (
          <div className="card">
            <EmptyState
              icon={<Clapperboard />}
              title="Не удалось загрузить офферы сети"
              action={<Button onClick={() => q.refetch()}>Повторить</Button>}
            >
              {(q.error as Error).message}
            </EmptyState>
          </div>
        ) : !all.length ? (
          <div className="card">
            <EmptyState icon={<Clapperboard />} title="Каталога сети ещё нет">
              Каталог buyo появится после подключения ключа сети в «Настройки → Интеграции».
            </EmptyState>
          </div>
        ) : !shown.length ? (
          <div className="card">
            <EmptyState
              icon={<Clapperboard />}
              title={filter === 'empty' ? 'У всех офферов есть креативы' : 'Ничего не нашлось'}
            >
              {filter === 'empty'
                ? 'Новые офферы без роликов появятся здесь сами.'
                : 'Попробуйте другое название.'}
            </EmptyState>
          </div>
        ) : (
          <ul className="net-list">
            {shown.map((o) => (
              <OfferRow key={o.extId} o={o} />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
